"use client";

import { useState } from "react";
import type { Project } from "@easyshot/shared";
import { formatVideoDuration, projectCoverVideo } from "@/lib/project-cover";

export function ProjectCover({ project, className = "size-20" }: { project: Project; className?: string }) {
  const source = projectCoverVideo(project);
  const savedDuration = project.jobs.find(job => job.status === "succeeded" && job.videoUrl === source)?.durationSec;
  return <FirstFrame key={`${project.id}:${source}`} source={source} savedDuration={savedDuration} name={project.name} className={className} />;
}

function FirstFrame({ source, savedDuration, name, className }: { source?: string; savedDuration?: number; name: string; className: string }) {
  const [failed, setFailed] = useState(false);
  const [actualDuration, setActualDuration] = useState<number>();
  const duration = formatVideoDuration(actualDuration) ?? formatVideoDuration(savedDuration);
  const readDuration = (video: HTMLVideoElement) => {
    if (Number.isFinite(video.duration) && video.duration > 0) setActualDuration(video.duration);
  };
  // Decode the first frame directly: works for existing videos without a new
  // generation, stored poster, cross-origin canvas export or paid API call.
  return <div className={`relative shrink-0 overflow-hidden rounded-xl bg-white/[0.05] ${className}`}>
    {source && !failed ? (
      <video src={`${source.split("#")[0]}#t=0.001`} aria-label={`Video first frame for ${name}`} muted playsInline preload="metadata" controls={false} disablePictureInPicture tabIndex={-1}
        onLoadedMetadata={event => { readDuration(event.currentTarget); event.currentTarget.currentTime = 0.001; }}
        onDurationChange={event => readDuration(event.currentTarget)}
        onError={() => setFailed(true)} className="pointer-events-none h-full w-full object-contain" />
    ) : <div role="img" aria-label={`Video cover unavailable for ${name}`} className="grid h-full w-full place-items-center text-2xl">🎬</div>}
    {source && <span aria-label={duration ? `Video duration ${duration}` : failed ? "Video duration unavailable" : "Reading video duration"} className="pointer-events-none absolute bottom-1 right-1 rounded bg-black/85 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-white">
      {duration ?? (failed ? "—" : "…")}
    </span>}
  </div>;
}
